import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

const SignUp: React.FC = () => {
    const [accountType, setAccountType] = useState<'user' | 'distributor'>('user');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        mobile: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        // Distributor specific fields
        businessName: '',
        gstNumber: '',
        panNumber: '',
        businessAddress: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            if (name === 'terms') {
                setAcceptedTerms(checked);
            } else if (name === 'privacy') {
                setAcceptedPrivacy(checked);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!acceptedTerms || !acceptedPrivacy) {
            setError('Please accept both Terms & Conditions and Privacy Policy to continue.');
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);
        try {
            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            const user = userCredential.user;
            // Prepare Firestore data
            const data = {
                email: formData.email,
                role: accountType,
                acceptedTerms: true,
                acceptedPrivacy: true,
                acceptedDate: new Date().toISOString(),
                name: formData.name,
                mobile: formData.mobile,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                businessName: formData.businessName,
                gstNumber: formData.gstNumber,
                panNumber: formData.panNumber,
                businessAddress: formData.businessAddress,
            };
            // Save user data to Firestore
            await setDoc(doc(db, 'users', user.uid), data);
            setSuccess('Account created successfully!');
            setFormData({
                email: '', password: '', name: '', mobile: '', address: '', city: '', state: '', pincode: '', businessName: '', gstNumber: '', panNumber: '', businessAddress: ''
            });
            setTimeout(() => {
                navigate('/signin');
            }, 1500); // 1.5s delay to show success message
        } catch (err: any) {
            setError(err.message || 'Failed to create account.');
        }
        setLoading(false);
    };

    // Add a helper to parse Firebase password errors
    const parseFirebaseError = (message: string) => {
        if (message.includes('auth/password-does-not-meet-requirements')) {
            return (
                <ul className="list-disc list-inside text-left">
                    <li>Password must contain an upper case character</li>
                    <li>Password must contain a non-alphanumeric character</li>
                </ul>
            );
        }
        if (message.includes('auth/email-already-in-use')) {
            return 'This email is already registered.';
        }
        if (message.includes('auth/invalid-email')) {
            return 'Please enter a valid email address.';
        }
        if (message.includes('auth/weak-password')) {
            return 'Password should be at least 6 characters.';
        }
        return message;
    };

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center px-4 py-6 sm:py-8">
            <div className="w-full max-w-xs sm:max-w-md">
                {/* Logo */}
                <div className="mb-6 sm:mb-8 text-center">
                    <img
                        src="/images/logo.png"
                        alt="Fixisy Logo"
                        className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4"
                    />
                    <h1 className="text-2xl sm:text-3xl font-bold text-white">Create Account</h1>
                </div>

                {/* Account Type Selection */}
                <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg font-semibold text-white mb-3 text-center">Choose Account Type</h3>
                    <div className="flex gap-2 sm:gap-4">
                        <button
                            type="button"
                            onClick={() => setAccountType('user')}
                            className={`flex-1 py-3 rounded-lg font-semibold text-sm sm:text-base transition-colors ${accountType === 'user'
                                ? 'bg-cyan-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                }`}
                        >
                            User
                        </button>
                        <button
                            type="button"
                            onClick={() => setAccountType('distributor')}
                            className={`flex-1 py-3 rounded-lg font-semibold text-sm sm:text-base transition-colors ${accountType === 'distributor'
                                ? 'bg-cyan-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                }`}
                        >
                            Distributor
                        </button>
                    </div>
                </div>

                {/* Download App Section */}
                <div className="mb-4 sm:mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-2 text-center">Download our app</h3>
                    <p className="text-gray-300 text-sm text-center mb-3">
                        Get the full Fixisy experience on your mobile device
                    </p>
                    <div className="flex gap-2 justify-center">
                        <button
                            type="button"
                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-semibold hover:bg-cyan-600 transition-colors"
                            onClick={() => window.open('https://play.google.com/store/apps', '_blank')}
                        >
                            Google Play
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-semibold hover:bg-cyan-600 transition-colors"
                            onClick={() => window.open('https://apps.apple.com', '_blank')}
                        >
                            App Store
                        </button>
                    </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="mb-2 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm text-center">
                        {typeof error === 'string' ? parseFirebaseError(error) : error}
                    </div>
                )}
                {success && <div className="mb-2 text-green-400 text-center text-sm">{success}</div>}

                {/* Sign Up Form */}
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />

                    <input
                        type="text"
                        name="name"
                        placeholder="Full Name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />
                    <input
                        type="tel"
                        name="mobile"
                        placeholder="Mobile Number"
                        value={formData.mobile}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />



                    <input
                        type="text"
                        name="address"
                        placeholder="Home Address"
                        value={formData.address}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />
                    <input
                        type="text"
                        name="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />
                    <input
                        type="text"
                        name="state"
                        placeholder="State"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />
                    <input
                        type="text"
                        name="pincode"
                        placeholder="Pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                        required
                    />

                    {/* Terms and Privacy Policy Checkboxes */}
                    <div className="space-y-2">
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="terms"
                                    name="terms"
                                    type="checkbox"
                                    checked={acceptedTerms}
                                    onChange={handleChange}
                                    className="w-4 h-4 border border-gray-600 rounded bg-gray-800 focus:ring-3 focus:ring-cyan-500"
                                    required
                                />
                            </div>
                            <div className="ml-3">
                                <label htmlFor="terms" className="text-sm text-gray-300">
                                    I agree to the{' '}
                                    <Link to="/terms" target="_blank" className="text-cyan-400 hover:text-cyan-300">
                                        Terms and Conditions
                                    </Link>
                                </label>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="privacy"
                                    name="privacy"
                                    type="checkbox"
                                    checked={acceptedPrivacy}
                                    onChange={handleChange}
                                    className="w-4 h-4 border border-gray-600 rounded bg-gray-800 focus:ring-3 focus:ring-cyan-500"
                                    required
                                />
                            </div>
                            <div className="ml-3">
                                <label htmlFor="privacy" className="text-sm text-gray-300">
                                    I agree to the{' '}
                                    <Link to="/privacy" target="_blank" className="text-cyan-400 hover:text-cyan-300">
                                        Privacy Policy
                                    </Link>
                                </label>
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-cyan-500 text-white py-3 rounded-lg font-semibold hover:bg-cyan-600 transition-colors text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={loading || !acceptedTerms || !acceptedPrivacy}
                    >
                        {loading ? 'Signing Up...' : 'Sign Up'}
                    </button>
                </form>

                {/* Sign In Link */}
                <p className="mt-6 sm:mt-8 text-center text-gray-400 text-xs sm:text-base">
                    Already have an account?{' '}
                    <Link to="/signin" className="text-cyan-400 hover:text-cyan-300">
                        Sign In
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default SignUp; 